import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import { i18n } from "lib/i18n";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { dispatchError, scrollToWithAnimation, generateRandomId } from "Utils";
import {
  getClassMaterialsFeedVariablesMemoized,
  getOutlineFeedQueryVariables,
  getToastMsgForResourceUpdate,
  getClassMaterialsSyncStatusVariablesMemoized,
  getClassFlowStudentsVariables,
} from "./utils";
import { getNestedChildren, isFolder } from "Utils/EntityFlowUtils";
import { updateSettings } from "modules/ConfigurationThunks";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  getClassMaterialItemFromCache,
  getClassMaterialsFeedReadAndWriteHelpers,
  getClassMaterialsOutlineFeedFromCache,
  writeClassMaterialsItemToCache,
  writeClassMaterialsOutlineFeedToCache,
} from "./ClassMaterialsGraphqlHelpers";
import {
  createClassMaterialResourceMutation,
  shareClassMaterialResourceMutation,
  importMaterialsFromUnitPlanMutation,
  reorderOrganizationResourcesMutation,
  deleteOrganizationResourcesMutation,
  updateOrganizationResourceMutation,
  updateAssignmentMutation,
  updateResourceIsNewMutation,
} from "./ClassMaterialsMutations";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";
import {
  getUserType,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import {
  getUpdateAssignmentDetailsFromCache,
  writeUpdateAssignmentDetailsInCache,
} from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getCourseDetails } from "Course/modules/CourseModule";
import {
  createAssessment,
  updateAssessment,
} from "Assessments/modules/AssessmentModule";
import { getAddedCoursesData } from "ClassRoom/components/AddAssignment/Utils";
import {
  createAssignment,
  getSimilarityIndexDefaultValue,
} from "ClassRoom/modules/ClassRoomModule";
import {
  getClassMaterialsFeedQuery,
  getClassMaterialsOutlineFeedQuery,
  getSyncStatusQuery,
  getClassFlowStudentsQuery,
} from "./ClassMaterialsQuery";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { CLASS_FLOW_EDIT_PERMISSION } from "Constants/permissionConstants";
import { MAX_DEPTH } from "UIComponents/NestedAccordion/constants";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";

export const canEditClassFlowItems = () => {
  return ACLStorage.getPermissionValue(CLASS_FLOW_EDIT_PERMISSION);
};

export const getIsNewExpandCollapseBehaviourEnabled = () => {
  return ACLStore.can("FeatureFlag:NewExpandCollapseFlow");
};

export const USER_CLASS_FLOW_SETTINGS = "UserClassFlowSettings";
export const USER_CLASS_FLOW_SETTING_OPTIONS = {
  BULK_ASSIGN_CONFIRMATION_SETTING: "ShowBulkAssignClassMaterialsDialog",
  SECTION_UNSHARE_CONFIRMATION: "ShowSectionUnshareConfirmationDialog",
  SHARE_RESOURCE_CONFIRMATION: "ShowShareResourceConfirmationDialog",
  ASSIGN_CONFIRMATION: "ShowAssignConfirmationDialog",
  SECTION_SHARE_WITH_UNSHARED_PARENT:
    "ShowSectionShareWithUnsharedParentDialog",
};

export const SHARE_ACTIONS = {
  SHARE: "SHARE",
  UNSHARE: "UNSHARE",
};
const PUBLISHED_STATES = ["ARCHIVED", "SCHEDULED"];

const DEFAULT_VISIBLE_TO = ["TEACHER"];

const DEFAULT_QUICK_ASSIGN_SETTINGS = {
  state: "PUBLISHED",
  isClassDiscussionEnabled: false,
  isStudentSubmissionEnabled: true,
  isTeacherEvaluationEnabled: false,
  isStudentEvaluationEnabled: false,
  isSimilarityCheckEnabled: false,
  isExcludedFromFinalGradeCalculation: false,
  isPrivate: false,
  isOnScreenCalculatorEnabled: false,
  showStudentResponse: "ONCE_EVALUATED",
  isTimedTestEnabled: false,
  isLockScreenEnabled: false,
  isLockSubmissionEnabled: false,
  isSharedWithClassMaterial: true,
};

export const importMaterialsFromUnitPlan = ({
  input,
  selectedCount,
  maxDepth,
  shouldAddToBottom = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const orgId = userInfo.org_id;
    const userType = getUserType(state);
    const courseId = state.teacher.selected_class.selected_course;

    const curriculumType = getCurrentCurriculumProgramType(state);

    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      userInfo,
      userType,
      curriculumType,
    });

    dispatch(setIsActionLoading(true));
    const outlineQueryVariables = getOutlineFeedQueryVariables({
      courseId,
      userInfo,
      userType,
      curriculumType,
    });

    const syncStatusVariables = getClassMaterialsSyncStatusVariablesMemoized({
      orgId,
      courseId,
    });

    const academicYearId = getSelectedAcademicYearId(state);
    const courseGradeFilters = {
      academicYearId,
    };

    // when student is added or removed from class-settings then getCourseDetailsQuery is not re-fetched
    // hence making network call to get the latest data instead of reading from cache.
    const course = await dispatch(
      getCourseDetails({
        id: courseId,
        courseGradeFilters,
      })
    );

    const studentIds = _.map(
      _.get(course, "students.edges", []),
      student => _.get(student, "node.id", null),
      []
    );

    const putAfterItemId = _.get(input, "putAfterItemId", null);

    const defaultPositioningOfNewItems = _.get(
      state,
      "classMaterials.displaySettings.add_to_top",
      false
    );
    const _addToTop = shouldAddToBottom ? false : defaultPositioningOfNewItems;
    const shouldAddtoTop = _addToTop && !putAfterItemId;

    input.addToTop = shouldAddtoTop;

    try {
      const { data } = await client.mutate({
        mutation: importMaterialsFromUnitPlanMutation,
        variables: {
          input: {
            ...input,
            studentIds,
            maxDepth: MAX_DEPTH,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getClassMaterialsFeedQuery,
            variables: feedQueryVariables,
          },
          {
            query: getClassMaterialsOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
          {
            query: getSyncStatusQuery,
            variables: syncStatusVariables,
          },
        ],
      });

      const response = _.get(
        data,
        "platform.importOrganizationResource",
        false
      );

      if (response) {
        const { isSuccess, error } = response;
        if (isSuccess) {
          // Might check later if we need to show a toast message
          // dispatch(
          //   setToastMsg({
          //     msg: "classMaterials:toast_msg_for_import_items",
          //     locale_params: [
          //       {
          //         key: "importCount",
          //         value: selectedCount,
          //         isPlainText: true,
          //       },
          //     ],
          //     type: "success",
          //     position: "toast-bottom-left",
          //   })
          // );
        } else {
          if (error == "IMPORT_DEPTH_ERROR") {
            dispatch(
              setToastMsg({
                msg: "classMaterials:disabled_overlay_text",
                locale_params: [
                  {
                    key: "maxDepth",
                    value: maxDepth,
                    isPlainText: true,
                  },
                ],
                position: "toast-bottom-left",
              })
            );
          } else {
            dispatch(
              setToastMsg({
                msg: "toastMsgs:something_went_wrong",
                position: "toast-bottom-left",
              })
            );
          }
        }
      }

      return response;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      dispatch(setIsActionLoading(false));
    }
  };
};

// function to update childrenHierarchyInfo of all ancestors (bottom to top) of a resource
const updateAncestorChildCountRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;

  if (node?.childrenHierarchyInfo) {
    node.childrenHierarchyInfo.totalCount += factor;
  }

  if (node?.parent) {
    updateAncestorChildCountRecursively({
      items,
      id: node.parent.id,
      factor,
    });
  }
};

// updates depth recursively
const updateDepthRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;
  node.depth += factor;

  const children = _.get(node, "childrenV2.edges", []);

  _.forEach(children, ({ node: childNode }) => {
    updateDepthRecursively({
      items,
      id: childNode.id,
      factor,
    });
  });
};

// recalculates max depth of all ancestors (bottom to top) of a resource
// this is required for drag and drop restrictions
const recalculateMaxDepth = ({ items, id }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const children = _.get(item, "node.childrenV2.edges", []);
  const childrenItems = _.chain(children)
    .map(child => {
      return _.find(items, ["node.id", child.node.id]);
    })
    .filter(child => Boolean(child))
    .value();

  const maxDepth =
    children.length == 0
      ? 1
      : _.reduce(
          childrenItems,
          (result, { node }) => {
            return Math.max(
              result,
              _.get(node, "childrenHierarchyInfo.maxDepth", 1) + 1
            );
          },
          0
        );

  if (item?.node?.childrenHierarchyInfo) {
    item.node.childrenHierarchyInfo.maxDepth = maxDepth;
  }

  const parent = _.find(items, ["node.id", item.node.parent?.id]);
  if (parent) {
    recalculateMaxDepth({ items, id: parent.node.id });
  }
};

const getResourceCreationToast = ({
  isCreatingResource = false,
  totalResources = 0,
}) => {
  return setToastMsg({
    msg: isCreatingResource
      ? totalResources > 1
        ? "classMaterials:toast_msg_for_multiple_resource_added"
        : "classMaterials:toast_msg_for_resource_added"
      : "classMaterials:toast_msg_for_section_added",
    locale_params: [
      {
        key: "label",
        value: "toastMsgs:created",
        isPlainText: false,
      },
      {
        key: "count",
        value: totalResources,
        isPlainText: true,
      },
    ],
    type: "success",
    position: "toast-bottom-left",
  });
};

export const createClassMaterialsResources = ({
  resources,
  visibleTo = DEFAULT_VISIBLE_TO,
  putAfterItemId: _putAfterItemId = null,
  setNewDataIds,
  courseId: _courseId,
  isFolderCollapsed: _isFolderCollapsed = false,
  sharingState = "DRAFT",
  showLoader = false,
  callback,
  quickCreation = false,
  sectionCreationType,
  parentId,
  newDepth: _newDepth,
  studentIds: _studentIds = null,
  shouldAddToBottom = false,
}) => {
  return async (dispatch, getState) => {
    if (_.isEmpty(resources)) return;

    const state = getState();
    const isInlineCreateOrEdit = _.get(
      state,
      "classMaterials.activeContentData.isInlineCreateOrEditItem",
      false
    );
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const userType = getUserType(state);
    const courseId = _courseId ?? state.teacher.selected_class.selected_course;
    const isCreatingAssignment = _.some(resources, {
      resourceType: "ASSIGNMENT",
    });
    const isCreatingResource = _.some(resources, {
      resourceType: "FILE",
    });

    const curriculumType = getCurrentCurriculumProgramType(state);

    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    const filters = _.get(state, "classMaterials.filters");
    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    const classMaterialsFeed = _.cloneDeep(
      getClassMaterialsFeed(feedQueryVariables)
    )?.organizationResourcesFlatFeed;

    // it is possible to pass a courseId from props when assigning to different classes
    // but we do not want to pass putAfterItemId or parentResourceId in that case
    // since we neither have the data nor it is expected
    const isInSelectedCourse =
      state.teacher.selected_class.selected_course === courseId;

    let putAfterItemId, isFolderCollapsed;

    if (isInSelectedCourse) {
      putAfterItemId = _putAfterItemId;
      isFolderCollapsed = _isFolderCollapsed;

      if (isCreatingAssignment) {
        const iInlineCreation = _.get(
          state,
          "classMaterials.activeContentData.isInlineCreateOrEditItem",
          false
        );
        const itemState = iInlineCreation
          ? "activeContentData"
          : "assignmentModalData";
        putAfterItemId = _.get(
          state,
          `classMaterials.${itemState}.putAfterItemId`,
          putAfterItemId
        );

        isFolderCollapsed = _.get(
          state,
          `classMaterials.${itemState}.isFolderCollapsed`,
          isFolderCollapsed
        );
      }
    }

    const putAfterItem = _.find(
      classMaterialsFeed?.edges,
      ({ node }) => node.id === putAfterItemId
    )?.node;

    const isSection = isFolder(putAfterItem);

    // when creating a section in parallel to current
    const isParallelSection = sectionCreationType === "PARALLEL";

    // when creating a subsection to the current section which was previously collapsed
    const isExpandedSubsection = sectionCreationType === "EXPANDED_SUBSECTION";

    // if putAfterItem is a section, then newly created resources are meant to go inside it
    // otherwise, we find the parent of putAfterItem
    let parent = putAfterItem;

    // if folder is collapsed, new item should be created after it
    const shouldPutInsideSection =
      isSection && !isFolderCollapsed && !isParallelSection;

    if (isInSelectedCourse && !shouldPutInsideSection) {
      const newParentId =
        isParallelSection || (isExpandedSubsection && parentId)
          ? parentId
          : putAfterItem?.parent?.id;

      parent = _.find(
        classMaterialsFeed?.edges,
        ({ node }) => node.id === newParentId
      )?.node;
    }

    const parentResourceId = shouldPutInsideSection
      ? putAfterItemId
      : parent?.id;

    if (showLoader) {
      dispatch(setIsActionLoading(true));
    }
    const outlineQueryVariables = getOutlineFeedQueryVariables({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    let studentIds = _studentIds;

    if (_.isNull(studentIds)) {
      const academicYearId = getSelectedAcademicYearId(state);
      const courseGradeFilters = {
        academicYearId,
      };

      // when student is added or removed from class-settings then getCourseDetailsQuery is not re-fetched
      // hence making network call to get the latest data instead of reading from cache.
      const course = await dispatch(
        getCourseDetails({
          id: courseId,
          courseGradeFilters,
        })
      );

      studentIds = _.map(
        _.get(course, "students.edges", []),
        student => _.get(student, "node.id", null),
        []
      );
    }

    const defaultPositioningOfNewItems = _.get(
      state,
      "classMaterials.displaySettings.add_to_top",
      false
    );

    const _addToTop = shouldAddToBottom ? false : defaultPositioningOfNewItems;
    const shouldAddtoTop = _addToTop && !putAfterItemId;

    try {
      return await client.mutate({
        mutation: createClassMaterialResourceMutation,
        variables: {
          studentIds: [],
          input: {
            parentType: "COURSE",
            parentId: courseId,
            shouldHandleSequence: true,
            putAfterItemId,
            parentResourceId,
            organizationResources: _.map(resources, resource => ({
              ...resource,
              visibleTo,
              type: "CLASS_MATERIAL",
              // in class stream, after all tasks are completed, the state is changed to ARCHIVED and moved to completed tab
              // but in class flow, there is no need for it
              state: _.includes(PUBLISHED_STATES, sharingState)
                ? "PUBLISHED"
                : sharingState,
              studentIds,
            })),
            addToTop: shouldAddtoTop,
          },
          filters: _.get(feedQueryVariables, "filters", {}),
          skipAssignedStudents: _.get(
            feedQueryVariables,
            "skipAssignedStudents",
            true
          ),
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getClassMaterialsOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
        ],
        update: (
          cache,
          {
            data: {
              platform: { createOrganizationResources },
            },
          }
        ) => {
          if (_.isEmpty(classMaterialsFeed)) return;

          let newDepth = 1;

          if (putAfterItem) {
            newDepth = putAfterItem.depth;
            if (shouldPutInsideSection) newDepth += 1;
          }

          if (isParallelSection && parent.depth) {
            newDepth = parent.depth + 1;
          }

          newDepth = _newDepth ?? newDepth;

          const createdResources = _.map(
            _.cloneDeep(createOrganizationResources),
            resource => ({
              __typename: "OrganizationResourceEdge",
              node: {
                __typename: "OrganizationResource",
                childrenV2: {
                  __typename: "OrganizationResourceFlatFeedConnection",
                  edges: [],
                },
                ...resource,
                ...(isFolder(resource) &&
                _.isEmpty(resource.childrenHierarchyInfo)
                  ? {
                      childrenHierarchyInfo: {
                        maxDepth: 1,
                        totalCount: 0,
                        totalCountWithFolder: 0,
                        __typename:
                          "OrganizationResourceChildrenHierarchyCount",
                      },
                    }
                  : {}),
                depth: resource.depth ?? newDepth,
                isNew: false,
              },
            })
          );

          if (putAfterItemId && parent) {
            parent.childrenV2.edges = [
              ...createdResources,
              ...parent.childrenV2.edges,
            ];

            const factor = _.reduce(
              createdResources,
              (acc, resource) => acc + (isFolder(resource.node) ? 0 : 1),
              0
            );

            updateAncestorChildCountRecursively({
              id: parent.id,
              items: classMaterialsFeed.edges,
              factor,
            });
          }
          const hasNextPage = _.get(classMaterialsFeed, "pageInfo.hasNextPage");

          // if put after item is present, then add in edges after the id
          if (putAfterItemId) {
            let index = _.findIndex(
              classMaterialsFeed.edges,
              ({ node }) => node.id === putAfterItemId
            );

            if (
              isSection &&
              (isFolderCollapsed || putAfterItem.depth >= newDepth)
            ) {
              const feedLength = classMaterialsFeed.edges.length;

              for (let i = index + 1; i < feedLength; i++) {
                const item = classMaterialsFeed.edges[i];
                if (item.node.depth <= newDepth) {
                  index = i - 1;
                  break;
                }

                // edge case: this is the last folder in the feed
                if (i === feedLength - 1) {
                  index = i;
                  break;
                }
              }
            }

            classMaterialsFeed.edges.splice(index + 1, 0, ...createdResources);
          } else {
            if (shouldAddtoTop) {
              classMaterialsFeed.edges = [
                ...createdResources,
                ...classMaterialsFeed.edges,
              ];
            } else if (!hasNextPage) {
              // we do not want to add in cache if the last item has not been fetched yet
              classMaterialsFeed.edges = [
                ...classMaterialsFeed.edges,
                ...createdResources,
              ];
            }
          }

          if (parent) {
            recalculateMaxDepth({
              items: classMaterialsFeed.edges,
              id: parent.id,
            });
          }

          const updatedFeed = {
            ...classMaterialsFeed,
            resourceTypeBasedCount: {
              ...classMaterialsFeed.resourceTypeBasedCount,
              totalCount:
                _.get(
                  classMaterialsFeed,
                  "resourceTypeBasedCount.totalCount",
                  0
                ) + createdResources?.length,
            },
          };

          const isNewExpandCollapseBehaviourEnabled = getIsNewExpandCollapseBehaviourEnabled();
          const createdIds = _.map(createdResources, item => item?.node?.id);
          setTimeout(() => {
            setNewDataIds?.(createdIds);
            writeClassMaterialsFeed({
              data: updatedFeed,
              variables: feedQueryVariables,
              courseId,
            });

            if (isNewExpandCollapseBehaviourEnabled) {
              _.forEach(createdResources, ({ node }) => {
                writeClassMaterialsItemToCache({
                  id: node.id,
                  data: node,
                  variables: feedQueryVariables,
                });
              });
            }
            if (isInlineCreateOrEdit) {
              dispatch(setActiveContentData({}));
            }
            // assignment creation already has a toast message
            if (!isCreatingAssignment && !quickCreation) {
              const totalResources = _.size(resources);
              dispatch(
                getResourceCreationToast({ isCreatingResource, totalResources })
              );
            }
          });

          // added this setTimeout to make sure new item is added before scrolling to bottom
          setTimeout(() => {
            const element = document.getElementById(
              "classMaterials-innerContainer"
            );
            // if putAfterItemId exists, that means we're creating item in middle of feed
            // we will scroll to bottom only if putAfterItemId doesn't exist
            if (element && !hasNextPage && !putAfterItemId && !shouldAddtoTop) {
              scrollToWithAnimation(element, element.scrollHeight, 500, () => {
                callback?.(createdIds);
              });
            }
          });
        },
      });
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
      dispatch(setIsActionLoading(false));
    }
  };
};

const getShareActionToastMsg = ({
  action,
  isSectionOnly,
  isResource,
  sharingState,
  childrenCount = 0,
  isListFiltered = false,
  assessmentName,
  hasLockedAcademicTermChildren = false,
}) => {
  const t = i18n.t;
  let msgLocale = "";
  let itemsSkippedText = "";
  const childrenCountText = childrenCount;

  if (action === "SHARE") {
    if (isResource) {
      msgLocale =
        sharingState == "DRAFT"
          ? "classMaterials:toast_msg_for_resource_edit"
          : "classMaterials:toast_msg_for_resource_share";
    } else if (isSectionOnly) {
      msgLocale = "classMaterials:toast_msg_for_section_share";
    } else if (childrenCount === 1 && !isListFiltered) {
      if (hasLockedAcademicTermChildren) {
        msgLocale = "classMaterials:toast_msg_for_section_shared";
        itemsSkippedText = t(
          "course:locked_grading_periond_items_were_skipped"
        );
      } else {
        msgLocale = "classMaterials:toast_msg_for_section_with_one_item_share";
      }
    } else if (hasLockedAcademicTermChildren) {
      msgLocale =
        "classMaterials:toast_msg_for_section_with_all_items_share_other";
      itemsSkippedText = t("course:locked_grading_periond_items_were_skipped");
    } else {
      msgLocale = "classMaterials:toast_msg_for_section_with_all_items_share";
    }
  } else {
    msgLocale = isResource
      ? "classMaterials:toast_msg_for_resource_unshare"
      : "classMaterials:toast_msg_for_section_unshare";
  }

  let msg = t(msgLocale, {
    childrenCount: childrenCountText,
    label: assessmentName,
    assignment_label: assessmentName,
  });

  if (hasLockedAcademicTermChildren && itemsSkippedText) {
    msg = `${msg} ${itemsSkippedText}`;
  }

  return msg;
};

const getDeleteActionToastMsg = ({
  shouldDeleteSectionContents,
  isResource,
  removeFromClass,
  isSection,
  isAssignment,
  isDeleting,
  countOfDeletedItems,
  assessmentName,
}) => {
  const t = i18n.t;
  const childrenCountText = isDeleting
    ? countOfDeletedItems
    : t("toastMsgs:removed");

  let msgLocale;
  if (isAssignment) {
    msgLocale = "classMaterials:toast_msg_for_assignment_delete";
  } else if (removeFromClass) {
    msgLocale = "classMaterials:toast_msg_for_assignment_remove_from_class";
  } else if (isResource) {
    msgLocale = "classMaterials:toast_msg_for_resource_delete";
  } else if (isSection) {
    msgLocale = shouldDeleteSectionContents
      ? "classMaterials:toast_msg_for_section_with_items_delete"
      : "classMaterials:toast_msg_for_section_delete";
  }

  return t(msgLocale, {
    childrenCount: childrenCountText,
    label: assessmentName,
    assignment_label: assessmentName,
  });
};

export const deleteClassMaterialsResources = ({
  ids: _ids,
  shouldDeleteSectionContents = false,
  childrenCount = 0,
  message = "DELETE",
  isResource,
  isSection,
  assessmentName,
  isAssignment,
  suppressAssignmentDeleteToast = false,
}) => {
  return async (dispatch, getState) => {
    const ids = _.compact(_ids);
    if (_.isEmpty(ids)) return;

    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const userType = getUserType(state);
    const courseId = state.teacher.selected_class.selected_course;
    const filters = _.get(state, "classMaterials.filters");
    const removeFromClass = message === "REMOVE";

    const curriculumType = getCurrentCurriculumProgramType(state);

    const input = {
      ids,
      removeWithChildren: shouldDeleteSectionContents,
    };

    const refetchQueries = [];
    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    if (isSection) {
      const outlineQueryVariables = getOutlineFeedQueryVariables({
        courseId,
        filters,
        userInfo,
        userType,
        curriculumType,
      });

      refetchQueries.push({
        query: getClassMaterialsOutlineFeedQuery,
        variables: outlineQueryVariables,
      });
    }

    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    const previousFeed = _.cloneDeep(getClassMaterialsFeed(feedQueryVariables))
      ?.organizationResourcesFlatFeed;

    let countOfDeletedItems = 0,
      optimisticResponseUpdated = false;

    try {
      await client.mutate({
        mutation: deleteOrganizationResourcesMutation,
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            deleteOrganizationResource: true,
            __typename: "PlatformMutations",
          },
        },
        update: () => {
          if (optimisticResponseUpdated) return;
          optimisticResponseUpdated = true;

          const classMaterialsFeed = _.cloneDeep(previousFeed);

          const classMaterialFeedNodes = _.map(
            classMaterialsFeed.edges,
            "node"
          );

          const items = _.filter(classMaterialFeedNodes, ({ id }) =>
            _.includes(ids, id)
          );

          // update children and count of parents
          _.forEach(items, item => {
            const parent = _.find(
              classMaterialFeedNodes,
              ({ id }) => id === item.parent?.id
            );

            if (parent) {
              parent.childrenV2.edges = _.filter(
                parent.childrenV2.edges,
                ({ node }) => node.id !== item.id
              );

              updateAncestorChildCountRecursively({
                id: parent.id,
                items: classMaterialsFeed.edges,
                factor: isFolder(item) ? 0 : -1,
              });

              recalculateMaxDepth({
                items: classMaterialsFeed.edges,
                id: parent.id,
              });
            }

            const nestedChildren = getNestedChildren({
              items: classMaterialFeedNodes,
              id: item.id,
            });

            if (isFolder(item)) {
              if (shouldDeleteSectionContents) {
                const ids = _.map(nestedChildren, "id");
                classMaterialsFeed.edges = _.filter(
                  classMaterialsFeed.edges,
                  ({ node }) => !_.includes(ids, node.id)
                );

                if (parent) {
                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: classMaterialsFeed.edges,
                    factor: -_.get(item, "childrenHierarchyInfo.totalCount", 0),
                  });
                }
              } else {
                // children should be pushed outwards after parent deletion
                // so we decrease depth and change parent
                _.forEach(nestedChildren, child => {
                  child.depth -= 1;

                  // only update parent of immediate children
                  // otherwise the remaining structure will come out flat
                  if (parent && child.parent?.id === item.id) {
                    parent.childrenV2.edges.push({
                      node: child,
                    });

                    child.parent = {
                      ...(child.parent ?? {}),
                      id: parent.id,
                    };
                  }
                });
              }
            } else {
              if (item.resourceType === "ASSIGNMENT") {
                const assignmentId = _.get(item, "item.id", "");
                const assignmentItem = getUpdateAssignmentDetailsFromCache({
                  id: assignmentId,
                  userArchivedFilters: {
                    academicYearId: getSelectedAcademicYearId(state),
                  },
                });

                if (assignmentItem?.isSharedWithClassMaterial) {
                  writeUpdateAssignmentDetailsInCache({
                    id: assignmentId,
                    data: {
                      ...assignmentItem,
                      isSharedWithClassMaterial: false,
                    },
                    userArchivedFilters: {
                      academicYearId: getSelectedAcademicYearId(state),
                    },
                  });
                }
              }
            }
          });

          countOfDeletedItems = _.reduce(
            items,
            (result, item) => {
              result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
              result += isFolder(item) ? 0 : 1;
              return result;
            },
            0
          );

          const updatedFeed = {
            ...classMaterialsFeed,
            edges: _.filter(
              classMaterialsFeed.edges,
              ({ node }) => !_.includes(ids, node.id)
            ),
            resourceTypeBasedCount: {
              ...classMaterialsFeed?.resourceTypeBasedCount,
              totalCount:
                _.get(
                  classMaterialsFeed,
                  "resourceTypeBasedCount.totalCount",
                  0
                ) - countOfDeletedItems,
            },
          };

          setTimeout(() => {
            writeClassMaterialsFeed({
              data: updatedFeed,
              variables: feedQueryVariables,
              courseId,
            });
          });
        },
        refetchQueries,
      });
      if (!suppressAssignmentDeleteToast) {
        dispatch(
          setToastMsg({
            msg: getDeleteActionToastMsg({
              shouldDeleteSectionContents:
                shouldDeleteSectionContents && childrenCount !== 0,
              isResource,
              removeFromClass,
              isSection,
              isAssignment,
              isDeleting: message == "DELETE",
              countOfDeletedItems,
              assessmentName,
            }),
            notString: true,
            type: "success",
            position: "toast-bottom-left",
          })
        );
      }
      return;
    } catch (error) {
      console.error(error);

      // Revert optimistic data on error
      writeClassMaterialsFeed({
        data: previousFeed,
        variables: feedQueryVariables,
        courseId,
      });

      dispatchError({ dispatch, e: error });
    }
  };
};

export const updateClassMaterialsResources = ({
  id,
  label,
  attachment,
  resourceType,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const userType = getUserType(state);
    const courseId = state.teacher.selected_class.selected_course;
    const filters = _.get(state, "classMaterials.filters");
    const isInlineCreateOrEdit = _.get(
      state,
      "classMaterials.activeContentData.isInlineCreateOrEditItem",
      false
    );

    const isResource = _.includes(["NOTE", "LINK", "FILE"], resourceType);

    const curriculumType = getCurrentCurriculumProgramType(state);
    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    const variables = {
      input: {
        id,
        label,
      },
    };

    if (attachment) {
      if (_.includes(["LINK", "NOTE"], resourceType)) {
        variables.input.attachment = attachment;
      } else {
        variables.input.attachment = {
          type: _.get(attachment, "type", "FILE"),
          name: label,
        };
      }
    }

    let outlineQueryVariables, outlineData, outlineItem, prevOutlineItem;
    if (!isResource) {
      outlineQueryVariables = getOutlineFeedQueryVariables({
        courseId,
        filters,
        userInfo,
        userType,
        curriculumType,
      });

      outlineData = _.cloneDeep(
        getClassMaterialsOutlineFeedFromCache(outlineQueryVariables)
      );

      outlineItem = _.find(
        outlineData?.organizationResourcesFlatFeedOutline,
        item => item.id === id
      );
      prevOutlineItem = _.cloneDeep(outlineItem);
    }

    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    const classMaterialsFeed = _.cloneDeep(
      getClassMaterialsFeed(feedQueryVariables)
    )?.organizationResourcesFlatFeed;
    const item = _.find(classMaterialsFeed.edges, ({ node }) => node.id === id);
    const prevItem = _.cloneDeep(item);

    try {
      await client.mutate({
        mutation: updateOrganizationResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            updateOrganizationResource: {
              id: generateRandomId(), // This is required to trigger optimistic update before response
              __typename: "OrganizationResource",
            },
            __typename: "PlatformMutations",
          },
        },
        update: () => {
          if (item) {
            item.node.label = label;
            if (item.node?.attachment) {
              item.node.attachment.name = label;
              if (item.node.attachment.type === "LINK") {
                item.node.attachment.url = attachment.url;
                item.node.attachment.thumbUrl = attachment.thumbUrl;
              } else if (item.node.attachment.type === "NOTE") {
                item.node.attachment.name = label;
                item.node.attachment.attachmentTitle = label;
                item.node.attachment.title = label;
              } else {
                item.node.attachment.name = label;
              }
            }

            setTimeout(() => {
              writeClassMaterialsFeed({
                data: classMaterialsFeed,
                variables: feedQueryVariables,
                courseId,
              });

              if (!isResource && outlineItem) {
                outlineItem.label = label;

                writeClassMaterialsOutlineFeedToCache({
                  data: outlineData,
                  variables: outlineQueryVariables,
                });
              }
            });

            if (isInlineCreateOrEdit) {
              dispatch(setActiveContentData({}));
            }
          }
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate(resourceType)));
      return;
    } catch (error) {
      console.error(error);

      // Revert optimistic data on error
      if (item && prevItem) {
        item.node = prevItem.node;

        writeClassMaterialsFeed({
          data: classMaterialsFeed,
          variables: feedQueryVariables,
          courseId,
        });

        if (!isResource && outlineItem && prevOutlineItem) {
          outlineItem = prevOutlineItem;
          writeClassMaterialsOutlineFeedToCache({
            data: outlineData,
            variables: outlineQueryVariables,
          });
        }

        if (isInlineCreateOrEdit) {
          dispatch(setActiveContentData({}));
        }
      }
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
    }
  };
};

// when dragging an item after a folder, the putAfterItemId needs to be changed
// because we might need to put the new item after the last child of the folder
const interpolatePutAfterId = ({
  items: _items,
  id,
  putAfterItemId: _putAfterItemId,
  depth,
}) => {
  // remove current item from items
  const items = _.filter(_items, item => item.node.id !== id);
  const itemIndex = _.findIndex(items, ["node.id", _putAfterItemId]);
  const item = items[itemIndex];

  if (!item) return _putAfterItemId;

  let putAfterItemId = _putAfterItemId;

  const isFolder = item.node.resourceType === "FOLDER";
  if (isFolder) {
    const folderDepth = item.node.depth;
    if (depth > folderDepth) {
      putAfterItemId = _putAfterItemId;
    } else {
      let i = itemIndex;
      while (i < items.length) {
        i++;

        const nextItem = items[i];

        const itemDepth = _.get(nextItem, "node.depth");

        if (itemDepth <= depth) {
          const prevItem = items[i - 1];
          const itemId = prevItem?.node?.id;

          putAfterItemId = itemId ?? putAfterItemId;
          break;
        }

        // reached the end, put after the last item
        if (i === items.length - 1) {
          putAfterItemId = nextItem?.node?.id;
          break;
        }
      }
    }
  }

  return putAfterItemId;
};

export const reorderClassMaterialsResources = params => {
  const { id, putAfterItemId, parentId, depth, isMovedByDragAndDrop } = params;

  return async (dispatch, getState) => {
    if (id == putAfterItemId || id == parentId) {
      // Sentry.captureMessage("Reordering item after itself", {
      //   extra: {
      //     ...params,
      //   },
      // });
      return;
    }

    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const userType = getUserType(state);
    const courseId = state.teacher.selected_class.selected_course;
    const filters = _.get(state, "classMaterials.filters");

    const curriculumType = getCurrentCurriculumProgramType(state);
    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    const classMaterialsFeed = _.cloneDeep(
      getClassMaterialsFeed(feedQueryVariables)
    )?.organizationResourcesFlatFeed;

    try {
      const classMaterialFeedNodes = _.map(classMaterialsFeed.edges, "node");

      const item = _.find(
        classMaterialsFeed.edges,
        ({ node }) => node.id === id
      );

      if (!item) return;

      // modify putAfterItemId to reflect the correct position in case of folders
      const _putAfterItemId = interpolatePutAfterId({
        id,
        putAfterItemId,
        items: classMaterialsFeed.edges,
        depth,
      });

      // remove the item from its current position and place after putAfterItem
      const newEdges = _.reduce(
        classMaterialsFeed.edges,
        (result, edge, i) => {
          // item was dragged and dropped to the first position
          if (i === 0 && !_putAfterItemId) {
            result.push(item);
            result.push(edge);
            return result;
          }

          if (edge.node.id === id) return result;

          // put it after putAfterItem
          if (edge.node.id === _putAfterItemId) {
            result.push(edge);
            result.push(item);
          } else {
            result.push(edge);
          }
          return result;
        },
        []
      );

      classMaterialsFeed.edges = newEdges;

      let lastItemId = item.node.id;

      // also move children after the item
      const nestedChildren = getNestedChildren({
        items: _.map(classMaterialsFeed.edges, "node"),
        id,
      });

      _.forEach(nestedChildren, child => {
        const childIndex = _.findIndex(
          classMaterialsFeed.edges,
          ({ node }) => node.id === child.id
        );

        if (childIndex !== -1) {
          const childItem = _.first(
            classMaterialsFeed.edges.splice(childIndex, 1)
          );

          const indexToMove = _.findIndex(classMaterialsFeed.edges, {
            node: { id: lastItemId },
          });

          classMaterialsFeed.edges.splice(indexToMove + 1, 0, childItem);

          lastItemId = child.id;
        }
      });

      const currentParentId = _.get(item, "node.parent.id");
      let switchParent = parentId !== currentParentId;

      // adding this condition for null and undefined comparisons
      if (_.isEmpty(currentParentId) && _.isEmpty(parentId)) {
        switchParent = false;
      }

      // remove from current parent's children and put in new parent's children
      if (switchParent) {
        const currentParent = _.find(classMaterialFeedNodes, {
          id: currentParentId,
        });

        if (currentParent) {
          currentParent.childrenV2.edges = _.filter(
            currentParent.childrenV2.edges,
            ({ node }) => node.id !== id
          );
          updateAncestorChildCountRecursively({
            id: currentParent.id,
            items: classMaterialsFeed.edges,
            factor: -(
              (isFolder(item.node) ? 0 : 1) +
              _.get(item, "node.childrenHierarchyInfo.totalCount", 0)
            ),
          });

          recalculateMaxDepth({
            items: classMaterialsFeed.edges,
            id: currentParent.id,
          });
        }

        if (parentId) {
          item.node.parent = {
            ...(item.node.parent ?? {}),
            id: parentId,
          };

          const parent = _.find(classMaterialFeedNodes, { id: parentId });
          if (parent) {
            parent.childrenV2.edges.push(item);
            updateAncestorChildCountRecursively({
              id: parentId,
              items: classMaterialsFeed.edges,
              factor:
                (isFolder(item.node) ? 0 : 1) +
                _.get(item, "node.childrenHierarchyInfo.totalCount", 0),
            });
          }
        } else item.node.parent = null;
      }

      const currentDepth = _.get(item, "node.depth", 1);
      const factor = depth - currentDepth;

      updateDepthRecursively({
        items: classMaterialsFeed.edges,
        id: item.node.id,
        factor,
      });

      recalculateMaxDepth({
        items: classMaterialsFeed.edges,
        id: item.node.id,
      });

      writeClassMaterialsFeed({
        data: classMaterialsFeed,
        variables: feedQueryVariables,
        courseId,
      });
      const outlineQueryVariables = getOutlineFeedQueryVariables({
        courseId,
        filters,
        userInfo,
        userType,
        curriculumType,
      });

      await client.mutate({
        mutation: reorderOrganizationResourcesMutation,
        variables: {
          input: {
            id,
            putAfterItemId,
            parentResourceId: parentId,
            switchParent,
          },
        },
        refetchQueries: [
          {
            query: getClassMaterialsOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
        ],
      });

      if (!isMovedByDragAndDrop) {
        let title;
        const resourceType = _.get(item, "node.resourceType", "");
        switch (resourceType) {
          case "FOLDER":
            title = "common:folder";
            break;
          case "ASSIGNMENT":
          case "ASSESSMENT": {
            title = "toastMsgs:assignment";
            break;
          }
          default:
            title = `toastMsgs:resource`;
        }

        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:label_successfully_moved",
            locale_params: [
              {
                key: "label",
                value: title,
                isPlainText: false,
              },
            ],
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

export const shareClassMaterialsResources = ({
  id,
  isSectionOnly = false,
  action = SHARE_ACTIONS.SHARE,
  sharingState = "PUBLISHED",
  addedCourses,
  deletedCourses,
  updatedCourses,
  newStudents = [],
  isResource,
  childrenCount,
  isListFiltered,
  isQuickShare = false,
  classStudents = [],
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const userType = getUserType(state);
    const courseId = state.teacher.selected_class.selected_course;
    const filters = _.get(state, "classMaterials.filters");
    let _newStudents = newStudents;

    const variables = {
      input: {
        resourceId: id,
        courseId,
        isSectionOnly,
        action,
        addedCourses,
        deletedCourses,
        updatedCourses,
        state: sharingState,
      },
    };

    if (isQuickShare) {
      const itemData = await client.query({
        query: getClassFlowStudentsQuery,
        variables: getClassFlowStudentsVariables({
          itemId: id,
        }),
      });
      let addedStudents = _.map(
        _.get(itemData, "data.multiNode[0].students.edges", []),
        "node.id"
      );
      if (_.size(addedStudents) === 0) {
        addedStudents = _.map(classStudents, "id");
      }
      _newStudents = _.filter(classStudents, student =>
        _.includes(addedStudents, student.id)
      );

      variables.input.editStudents = { removedStudents: [], addedStudents };
    }

    const curriculumType = getCurrentCurriculumProgramType(state);
    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    const outlineQueryVariables = getOutlineFeedQueryVariables({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      filters,
      userInfo,
      userType,
      curriculumType,
    });

    const previousClassMaterialsFeed = _.cloneDeep(
      getClassMaterialsFeed(feedQueryVariables)
    )?.organizationResourcesFlatFeed;

    let immediateChildrenCount,
      isFolder = false,
      hasLockedAcademicTermChildren = false;

    const updateFunction = () => {
      const classMaterialsFeed = _.cloneDeep(previousClassMaterialsFeed);

      const item = _.find(
        classMaterialsFeed.edges,
        ({ node }) => node.id === id
      );

      if (item?.node) {
        const updatedStudents = {
          totalCount: _.size(_newStudents),
          __typename: "OrganizationResourceStudentConnection",
        };

        const resourceType = item.node.resourceType;
        isFolder = resourceType === "FOLDER";

        _.set(item, "node.state", sharingState);
        _.set(item, "node.students", updatedStudents);

        if (isFolder && !isSectionOnly) {
          const nestedChildren = getNestedChildren({
            items: _.map(classMaterialsFeed.edges, "node"),
            id: id,
          });

          _.forEach(nestedChildren, child => {
            if (child.resourceType === "ASSIGNMENT") {
              _.set(child, "item.state.state", sharingState);
            } else {
              child.state = sharingState;
              if (!child.students?.totalCount) {
                child.students = updatedStudents;
              }
            }
          });
        }

        setTimeout(() =>
          writeClassMaterialsFeed({
            data: classMaterialsFeed,
            variables: feedQueryVariables,
            courseId,
          })
        );
      }

      immediateChildrenCount = _.size(_.get(item, "node.childrenV2.edges", []));
    };

    const updateFunctionV2 = () => {
      const item = _.cloneDeep(
        getClassMaterialItemFromCache({
          id,
          variables: feedQueryVariables,
        })
      );

      if (item) {
        const updatedStudents = {
          totalCount: _.size(_newStudents),
          __typename: "OrganizationResourceStudentConnection",
        };

        const resourceType = item.resourceType;
        isFolder = resourceType === "FOLDER";

        _.set(item, "state", sharingState);
        _.set(item, "students", updatedStudents);

        if (isFolder && !isSectionOnly) {
          const nestedChildren = getNestedChildren({
            items: _.map(previousClassMaterialsFeed.edges, "node"),
            id: id,
          });

          _.forEach(nestedChildren, child => {
            const academicTerm = _.get(child, "item.content.academicTerm");
            const assessmentType = _.get(
              child,
              "item.content.assessmentType.value"
            );
            const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
              academicTerm,
              assessmentType
            );
            hasLockedAcademicTermChildren =
              hasLockedAcademicTermChildren || !isGradingPeriodUnlocked;

            // If grading period is locked then the item's shared settings cannot be updated
            if (!isGradingPeriodUnlocked) return;

            const childData = _.cloneDeep(
              getClassMaterialItemFromCache({
                id: child.id,
                variables: feedQueryVariables,
              })
            );

            if (childData.resourceType === "ASSIGNMENT") {
              _.set(childData, "item.state.state", sharingState);
            } else {
              childData.state = sharingState;
              if (!childData.students?.totalCount) {
                childData.students = updatedStudents;
              }
            }

            writeClassMaterialsItemToCache({
              id: childData.id,
              data: childData,
              variables: feedQueryVariables,
            });
          });
        }

        setTimeout(() =>
          writeClassMaterialsItemToCache({
            id,
            data: item,
            variables: feedQueryVariables,
          })
        );
      }

      immediateChildrenCount = _.size(_.get(item, "childrenV2.edges", []));
    };

    const isNewExpandCollapseBehaviourEnabled = getIsNewExpandCollapseBehaviourEnabled();

    try {
      await client.mutate({
        mutation: shareClassMaterialResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            shareClassMaterialResources: true,
            __typename: "PlatformMutations",
          },
        },
        update: () =>
          isNewExpandCollapseBehaviourEnabled
            ? updateFunctionV2()
            : updateFunction(),
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getClassMaterialsOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
        ],
      });
      dispatch(
        setToastMsg({
          msg: getShareActionToastMsg({
            action,
            isSectionOnly:
              isSectionOnly || (isFolder && immediateChildrenCount == 0),
            isResource,
            sharingState,
            childrenCount,
            isListFiltered,
            hasLockedAcademicTermChildren,
          }),
          locale_params: [
            {
              key: "childrenCount",
              value: childrenCount,
              isPlainText: true,
            },
          ],
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );
      return;
    } catch (error) {
      console.error(error);
      setTimeout(() =>
        writeClassMaterialsFeed({
          data: previousClassMaterialsFeed,
          variables: feedQueryVariables,
          courseId,
        })
      );
      dispatchError({ dispatch, e: error });
    }
  };
};

export const quickAssignAssignment = ({
  item,
  students,
  assessmentName,
  courseSubjectLevels,
  isBackdateAssignment,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const userType = getUserType(state);
    const courseId = state.teacher.selected_class.selected_course;
    const filters = _.get(state, "classMaterials.filters");

    const curriculumType = getCurrentCurriculumProgramType(state);
    const isDP = _.isEqual(CURRICULUM_TYPE_DP, curriculumType);

    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    dispatch(setIsActionLoading(true));

    try {
      const assignment = item?.item;
      const studentIds = _.map(students, "id");

      const itemData = await client.query({
        query: getClassFlowStudentsQuery,
        variables: getClassFlowStudentsVariables({
          itemId: item.id,
        }),
      });

      const { id, assignedStudents } = _.get(
        itemData,
        "data.multiNode[0].item",
        {}
      );

      const currentStudentIds = _.map(assignedStudents, "student.id");
      const addedStudents = _.size(assignedStudents)
        ? _.intersection(studentIds, currentStudentIds)
        : studentIds;
      const removedStudents = _.difference(currentStudentIds, studentIds);
      const assignmentSettings = _.mapValues(
        DEFAULT_QUICK_ASSIGN_SETTINGS,
        (defaultValue, key) => {
          if (key == "state") return defaultValue;
          else return _.get(assignment, key, defaultValue);
        }
      );

      const assignmentSubjectLevelField = _.find(
        _.get(assignment, "content.fields", []),
        { uid: "subjectLevel" }
      );

      if (isDP && !_.isEmpty(assignmentSubjectLevelField)) {
        const assignmentSubjectLevels =
          _.get(assignmentSubjectLevelField, "value", []) ?? [];

        // If subject level is not selected then select all subject levels
        if (assignmentSubjectLevels?.length === 0) {
          const subjectLevels = _.map(courseSubjectLevels, "id");
          await dispatch(
            updateAssessment({
              assessmentId: _.get(assignment, "content.id", ""),
              hasAddRemoveValues: true,
              fieldUID: "subjectLevel",
              id: _.get(assignmentSubjectLevelField, "id", ""),
              value: subjectLevels,
              valuesToAdd: subjectLevels,
              valuesToRemove: [],
            })
          );
        }
      }

      return await client.mutate({
        mutation: updateAssignmentMutation,
        variables: {
          input: {
            id,
            courses: {
              updatedCourses: [
                {
                  courseId,
                  students: {
                    addedStudents,
                    removedStudents,
                  },
                  isBackdateAssignment,
                },
              ],
            },
            ...assignmentSettings,
          },
          studentIds,
        },
        update: (
          cache,
          {
            data: {
              platform: { updateAssignmentV2 },
            },
          }
        ) => {
          const newAssignment = updateAssignmentV2?.response;

          const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
            courseId,
            filters,
            userInfo,
            userType,
            curriculumType,
          });

          const classMaterialsFeed = _.cloneDeep(
            getClassMaterialsFeed(feedQueryVariables)
          )?.organizationResourcesFlatFeed;

          const classMaterialsNode = _.find(
            classMaterialsFeed.edges,
            ({ node }) => node.id === item.id
          );

          const assignmentItem = _.get(classMaterialsNode, "node.item");

          if (newAssignment && assignmentItem) {
            const newState = _.get(newAssignment, "state", {});
            const newAssignedStudents = _.get(
              newAssignment,
              "assignedStudents",
              {}
            );

            _.set(assignmentItem, "state", newState);
            _.set(assignmentItem, "assignedStudents", newAssignedStudents);
          }

          writeClassMaterialsFeed({
            data: classMaterialsFeed,
            variables: feedQueryVariables,
            courseId,
          });

          dispatch(
            setToastMsg({
              msg: "classMaterials:toast_msg_for_assignment_assign",
              locale_params: [
                {
                  key: "assignment_label",
                  value: assessmentName,
                  isPlainText: true,
                },
              ],
              type: "success",
              position: "toast-bottom-left",
            })
          );
        },
      });
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      dispatch(setIsActionLoading(false));
    }
  };
};

export const handleQuickCreateAndEdit = ({
  item,
  isSaving = true,
  setNewDataIds,
  quickCreation = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      type,
      putAfterItemId,
      label,
      isFolderCollapsed,
      sectionCreationType,
      parent,
      depth,
      shouldAddToBottom,
    } = item;

    if (isSaving && Boolean(label.trim())) {
      if (!quickCreation) {
        dispatch(
          setActiveContentData({
            ...item,
            isSavingInline: true,
          })
        );
      }

      switch (type) {
        case "CREATE_SECTION": {
          const parentId = parent?.id;
          if (quickCreation) {
            dispatch(getResourceCreationToast({}));
          }
          dispatch(
            createClassMaterialsResources({
              putAfterItemId,
              setNewDataIds,
              resources: [
                {
                  label,
                  resourceType: "FOLDER",
                },
              ],
              isFolderCollapsed,
              quickCreation,
              sectionCreationType,
              parentId,
              newDepth: depth,
              shouldAddToBottom,
            })
          );

          break;
        }

        case "FILE_RENAME":
        case "SECTION_RENAME": {
          dispatch(
            updateClassMaterialsResources({
              id: item.id,
              label,
              resourceType: type == "FILE_RENAME" ? "FILE" : "SECTION",
              attachment: _.get(item, "attachment", null),
            })
          );

          break;
        }
        default: {
          if (item?.label) {
            const curriculumProgramId = getSelectedCurriculumId(state);
            const selectedCurriculum = getCurrentCurriculumProgramType(state);
            const isDP = _.isEqual(CURRICULUM_TYPE_DP, selectedCurriculum);

            if (quickCreation) {
              dispatch(
                setToastMsg({
                  msg: "toastMsgs:action_successfully",
                  locale_params: [
                    {
                      key: "label",
                      value: "common:assignment",
                      isPlainText: false,
                    },
                    {
                      key: "action",
                      value: "toastMsgs:created",
                      isPlainText: false,
                    },
                  ],
                  type: "success",
                })
              );
            }
            const academicYearId = getSelectedAcademicYearId(state);
            const courseGradeFilters = {
              academicYearId,
            };

            // when student is added or removed from class-settings then getCourseDetailsQuery is not re-fetched
            // hence making network call to get the latest data instead of reading from cache.
            const course = await dispatch(
              getCourseDetails({
                id: item.courseId,
                courseGradeFilters,
              })
            );

            try {
              const assessmentType = _.get(item, "item.assessmentType", null);
              const groupType = _.get(item, "item.groupType", null);
              const fields = [];

              if (isDP && groupType !== "QUICK_TASK") {
                const subjectLevels = _.filter(
                  _.get(course, "genericTags", []),
                  {
                    type: "SUBJECT_LEVEL",
                  }
                );

                if (_.size(subjectLevels) == 1) {
                  fields.push({
                    uid: "subjectLevel",
                    value: [_.get(subjectLevels[0], "id", null)],
                  });
                }
              }

              const assignementId = await dispatch(
                createAssessment({
                  assessmentType,
                  title: item.label,
                  groupType,
                  unitPlanObj: {
                    unitPlanId: -1,
                  },
                  course,
                  fields,
                })
              );

              const students = _.map(
                _.get(course, "students.edges", []),
                student => _.get(student, "node", null),
                []
              );

              const updatedCourse = update(course, {
                students: { $set: students },
              });

              const { courses } = getAddedCoursesData({
                addedCourses: [updatedCourse],
              });

              const contentType = "ASSESSMENT";
              const isSimilarityCheckEnabled = dispatch(
                getSimilarityIndexDefaultValue()
              );

              if (assignementId) {
                await dispatch(
                  createAssignment({
                    unitPlanTemplateId: "",
                    leId: assignementId,
                    importedLeId: "",
                    creationMode: type,
                    selectedCourse: {},
                    folderNames: [],
                    courses,
                    contentType,
                    resource: {
                      label: "",
                      attachments: [],
                      title: "",
                      studentTemplates: [],
                    },
                    isSharedWithClassMaterial: true,
                    curriculumProgramId,
                    creationSource: "CLASS_MATERIALS",
                    isScheduledAssignment: false,
                    state: "DRAFT",
                    setNewDataIds,
                    isUnitDelinked: true,
                    isDuplicateAssignment: false,
                    putAfterItemId,
                    isClassDiscussionEnabled:
                      contentType == "ASSIGNMENT_DISCUSSION",
                    isStudentSubmissionEnabled: contentType == "ASSESSMENT",
                    isTeacherEvaluationEnabled: false,
                    isStudentEvaluationEnabled: false,
                    isOnScreenCalculatorEnabled: false,
                    showStudentResponse: "ONCE_EVALUATED",
                    isExcludedFromFinalGradeCalculation: false,
                    isPrivate: false,
                    isTimedTestEnabled: false,
                    isLockScreenEnabled: false,
                    isSafeBrowserEnabled: false,
                    isLockSubmissionEnabled: false,
                    isSimilarityCheckEnabled,
                    hideSuccessToast: quickCreation,
                  })
                );
              }
            } catch (e) {
              dispatch(setActiveContentData({}));
              console.error(e);
            }
          }
        }
      }
    } else {
      dispatch(setActiveContentData({}));
    }
  };
};

export const getUserClassFlowSettingValue = ({ userClassFlowSettings, key }) =>
  _.get(userClassFlowSettings, key, true);

export const getUserClassFlowSettings = () => {
  const userClassFlowSettings = getSettingValue({
    name: USER_CLASS_FLOW_SETTINGS,
    readFromUserAndOrgLevel: true,
  });

  return userClassFlowSettings;
};

export const updateUserClassFlowSettings = ({ key, value }) => {
  return async dispatch => {
    const userClassFlowSettings = getUserClassFlowSettings();

    const newSettings = {
      ...userClassFlowSettings,
      [key]: value,
    };

    dispatch(
      updateSettings({
        settings: [
          {
            name: USER_CLASS_FLOW_SETTINGS,
            value: JSON.stringify(newSettings),
          },
        ],
        showToastMsg: false,
      })
    );
  };
};

export const updateResourceIsNewState = id => {
  return async (dispatch, getState) => {
    const state = getState();
    const courseId = state.teacher.selected_class.selected_course;
    const filters = _.get(state, "classMaterials.filters");

    const curriculumType = getCurrentCurriculumProgramType(state);

    const {
      getClassMaterialsFeed,
      writeClassMaterialsFeed,
    } = getClassMaterialsFeedReadAndWriteHelpers();

    const feedQueryVariables = getClassMaterialsFeedVariablesMemoized({
      courseId,
      filters,
      userInfo: getUserInfo({ portalType: "PLANNER" }),
      userType: getUserType(state),
      curriculumType,
    });

    const previousData = getClassMaterialsFeed(feedQueryVariables);
    const previousFeed = _.get(
      previousData,
      "organizationResourcesFlatFeed",
      {}
    );

    try {
      return client.mutate({
        mutation: updateResourceIsNewMutation,
        variables: {
          ids: [id],
        },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            updateResourceIsNewState: true,
            __typename: "PlatformMutations",
          },
        },
        update: () => {
          const edges = _.get(previousFeed, "edges", []);
          const itemIndex = _.findIndex(edges, ({ node }) => node.id === id);

          if (itemIndex !== -1 && edges[itemIndex]?.node?.isNew) {
            const updatedFeed = update(previousFeed, {
              edges: {
                [itemIndex]: {
                  node: {
                    isNew: { $set: false },
                  },
                },
              },
            });

            setTimeout(() => {
              writeClassMaterialsFeed({
                data: updatedFeed,
                variables: feedQueryVariables,
                courseId,
              });
            });
          }
        },
      });
    } catch (error) {
      console.error(error);
      writeClassMaterialsFeed({
        data: previousFeed,
        variables: feedQueryVariables,
        courseId,
      });

      dispatchError({ dispatch, e: error });
    }
  };
};

const NAME = "ClassMaterials";
const SET_ASSIGNMENT_MODAL_DATA = `SET_ASSIGNMENT_MODAL_DATA_${NAME}`;
const SET_ADD_NOTE_MODAL_DATA = `SET_ADD_NOTE_MODAL_DATA_${NAME}`;
const SET_ADD_LINK_MODAL_DATA = `SET_ADD_LINK_MODAL_DATA_${NAME}`;
const SET_CONFIRM_MODAL_DATA = `SET_CONFIRM_MODAL_DATA_${NAME}`;
const SET_MOVE_TO_SECTION_MODAL_DATA = `SET_MOVE_TO_SECTION_MODAL_DATA_${NAME}`;
const SET_ACTIVE_CONTENT_DATA = `SET_SECTION_CREATION_${NAME}`;
const SET_ACTION_LOADING = `SET_ACTION_LOADING_${NAME}`;
const UPDATE_DISPLAY_SETTINGS = `UPDATE_DISPLAY_SETTINGS_${NAME}`;
const UPDATE_FILTERS = `UPDATE_FILTERS_${NAME}`;
const RESET_FILTERS = `RESET_FILTERS_${NAME}`;
const RESET_MODAL_STATE = `RESET_MODAL_STATE_${NAME}`;
const SET_IMPORT_FROM_ANOTHER_CLASS = `SET_IMPORT_FROM_ANOTHER_CLASS_${NAME}`;
const SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS = `SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS_${NAME}`;

export const DEFAULT_UNIT_FLOW_IMPORT_FILTERS = {
  searchText: "",
  selectedItemTypes: "ALL",
};

const initialState = {
  assignmentModalData: {},
  addNoteModalData: {},
  addLinkModalData: {},
  confirmationDialogData: {},
  moveToSectionModalData: {},
  importFromAnotherClassModalData: {},
  activeContentData: {},
  isActionLoading: false,
  displaySettings: {
    show_outline: true,
    collapse_all_sections: false,
    expand_all_sections: true,
    thumbnail_image: true,
    task_resource_type: false,
    task_status: true,
    assessment_category: true,
    due_or_scheduled_date: true,
    locked_status: true,
    add_to_top: false,
    add_to_bottom: true,
  },
  filters: {
    selectedItemTypes: "ALL",
    selectedCategories: "ALL",
    selectedStudents: "ALL",
    selectedAddedBy: "ALL",
    selectedStudentSubmissionStatus: "ALL",
    searchText: "",
    selectedTaskTypeIds: "ALL",
  },
};

export const setAssignmentModalData = data => ({
  type: SET_ASSIGNMENT_MODAL_DATA,
  data,
});

export const setAddNoteModalData = data => ({
  type: SET_ADD_NOTE_MODAL_DATA,
  data,
});

export const setAddLinkModalData = data => ({
  type: SET_ADD_LINK_MODAL_DATA,
  data,
});

export const setConfirmationDialogData = data => ({
  type: SET_CONFIRM_MODAL_DATA,
  data,
});

export const setMoveToSectionModalData = data => ({
  type: SET_MOVE_TO_SECTION_MODAL_DATA,
  data,
});

export const setActiveContentData = data => ({
  type: SET_ACTIVE_CONTENT_DATA,
  data,
});

export const setIsActionLoading = data => ({
  type: SET_ACTION_LOADING,
  data,
});

export const updateDisplaySettings = data => {
  return {
    type: UPDATE_DISPLAY_SETTINGS,
    data,
  };
};

export const updateFilters = data => {
  return {
    type: UPDATE_FILTERS,
    data,
  };
};

export const resetFilters = () => {
  return {
    type: RESET_FILTERS,
  };
};

export const resetModalState = () => {
  return {
    type: RESET_MODAL_STATE,
  };
};

export const setImportFromAnotherClassModalData = data => {
  return {
    type: SET_IMPORT_FROM_ANOTHER_CLASS,
    data,
  };
};

export const setImportFromAnotherClassFilters = data => {
  return {
    type: SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS,
    data,
  };
};

const REDUCER_HANDLERS = {
  [SET_ASSIGNMENT_MODAL_DATA]: (state, action) => {
    return update(state, { assignmentModalData: { $set: action.data } });
  },
  [SET_ADD_NOTE_MODAL_DATA]: (state, action) => {
    return update(state, { addNoteModalData: { $set: action.data } });
  },
  [SET_ADD_LINK_MODAL_DATA]: (state, action) => {
    return update(state, { addLinkModalData: { $set: action.data } });
  },
  [SET_CONFIRM_MODAL_DATA]: (state, action) => {
    return update(state, { confirmationDialogData: { $set: action.data } });
  },
  [SET_MOVE_TO_SECTION_MODAL_DATA]: (state, action) => {
    return update(state, { moveToSectionModalData: { $set: action.data } });
  },
  [SET_ACTIVE_CONTENT_DATA]: (state, action) => {
    return update(state, { activeContentData: { $set: action.data } });
  },
  [SET_ACTION_LOADING]: (state, action) => {
    return update(state, { isActionLoading: { $set: action.data } });
  },
  [UPDATE_DISPLAY_SETTINGS]: (state, action) => {
    return update(state, { displaySettings: { $set: action.data } });
  },
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;
    _.map(Object.keys(params), key => {
      state = update(state, {
        filters: { [key]: { $set: params[key] } },
      });
    });

    return state;
  },
  [RESET_FILTERS]: state => {
    return update(state, {
      filters: { $set: initialState.filters },
    });
  },
  [RESET_MODAL_STATE]: () => {
    return initialState;
  },
  [SET_IMPORT_FROM_ANOTHER_CLASS]: (state, action) => {
    return update(state, {
      importFromAnotherClassModalData: { $set: action.data },
    });
  },
  [SET_IMPORT_FROM_ANOTHER_CLASS_FILTERS]: (state, action) => {
    const data = {
      ..._.get(state, "importFromAnotherClassModalData.filters", {}),
      ..._.get(action, "data", {}),
    };

    return update(state, {
      importFromAnotherClassModalData: {
        filters: { $set: data },
      },
    });
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
